export type ChartType = "pie" | "bar" | "line" | "donut" | "table" | "crosstab";

export const CHART_COLORS = [
  "#ffb09c", // orange
  "#57dfbe", // green
  "#757679", // black
  "#a1a7df", // purple
  "#c36179", // crimson
  "#57a68c", // green (darker)
  "#57bfe7", // blue
  "#ebdd7c", // yellow
];

export const DEFAULT_CHART_CATEGORIES = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

export const isDonutChart = (chartType: ChartType): boolean => {
  return chartType === "donut";
};

export const getChartColor = (index: number): string => {
  return CHART_COLORS[index % CHART_COLORS.length];
};

/**
 * Interpolates between two hex colors based on a factor (0-1)
 * @param color1 - Starting color in hex format (e.g., "#ff0000")
 * @param color2 - Ending color in hex format (e.g., "#00ff00")
 * @param factor - Interpolation factor between 0 and 1
 * @returns Interpolated color in hex format
 */
export const interpolateColor = (
  color1: string,
  color2: string,
  factor: number,
): string => {
  const hex1 = color1.replace("#", "");
  const hex2 = color2.replace("#", "");

  const r1 = parseInt(hex1.slice(0, 2), 16);
  const g1 = parseInt(hex1.slice(2, 4), 16);
  const b1 = parseInt(hex1.slice(4, 6), 16);

  const r2 = parseInt(hex2.slice(0, 2), 16);
  const g2 = parseInt(hex2.slice(2, 4), 16);
  const b2 = parseInt(hex2.slice(4, 6), 16);

  const r = Math.round(r1 + (r2 - r1) * factor);
  const g = Math.round(g1 + (g2 - g1) * factor);
  const b = Math.round(b1 + (b2 - b1) * factor);

  return `#${r.toString(16).padStart(2, "0")}${g.toString(16).padStart(2, "0")}${b.toString(16).padStart(2, "0")}`;
};

/**
 * Transforms line chart API response data into the format expected by LineChartSeries
 * @param apiData - Raw API response data
 * @returns Transformed data with series and categories
 */
export interface LineChartApiResponse {
  question: {
    id: string;
    label: string;
    name: string;
    responseCount: number;
  };
  data: Array<{
    variableId: string;
    variableLabel: string;
    surveys: Array<{
      descriptiveMetrics: Array<{
        valueMetric: string;
        value: number | null;
        baseN: number;
        suppressed: boolean;
        crossVariableLabel: string;
        timePeriodLabel: string | null;
        statSignificance: number;
        missingRollingPeriodLabels?: string[];
      }>;
    }>;
  }>;
  weightsApplied: boolean;
  responsesMinDate?: string; // ISO date string (YYYY-MM-DD)
  responsesMaxDate?: string; // ISO date string (YYYY-MM-DD)
  effectiveRollingPeriodCount?: number; // Actual periods used (for partial windows)
  rollingPeriodsWarningMessage?: string; // Warning message if insufficient data
  comparisonRollingLabel?: string; // Formatted string of periods used (e.g., "vs. Q2 2025 - Q3 2025")
  missingRollingPeriodLabels?: string[];
}

export interface TransformedLineChartData {
  series: Array<{
    id: string;
    name: string;
    data: Array<{
      x: string;
      y: number | null;
      statSignificance?: number;
      baseN?: number;
      missingRollingPeriodLabels?: string[];
    }>;
    color?: string;
    visible?: boolean;
  }>;
  categories: string[];
  responsesMinDate?: string;
  responsesMaxDate?: string;
  effectiveRollingPeriodCount?: number;
  rollingPeriodsWarningMessage?: string;
  comparisonRollingLabel?: string;
  missingRollingPeriodLabels?: string[];
}

export const transformLineChartData = (
  apiData: LineChartApiResponse,
): TransformedLineChartData => {
  if (!apiData || !apiData.data || apiData.data.length === 0) {
    return {
      series: [],
      categories: [],
      responsesMinDate: apiData?.responsesMinDate,
      responsesMaxDate: apiData?.responsesMaxDate,
      effectiveRollingPeriodCount: apiData?.effectiveRollingPeriodCount,
      rollingPeriodsWarningMessage: apiData?.rollingPeriodsWarningMessage,
      comparisonRollingLabel: apiData?.comparisonRollingLabel,
      missingRollingPeriodLabels: apiData?.missingRollingPeriodLabels,
    };
  }

  // Extract all unique time periods (categories) from the first variable
  const categories: string[] = [];
  if (apiData.data[0]?.surveys[0]?.descriptiveMetrics) {
    apiData.data[0].surveys[0].descriptiveMetrics.forEach((metric) => {
      if (!categories.includes(metric.crossVariableLabel)) {
        categories.push(metric.crossVariableLabel);
      }
    });
  }

  // Transform each variable into a series
  const series = apiData.data.map((variable, index) => {
    const dataPoints =
      variable.surveys[0]?.descriptiveMetrics.map((metric) => ({
        x: metric.crossVariableLabel,
        y: metric.value,
        ...(metric.statSignificance !== undefined && {
          statSignificance: metric.statSignificance,
        }),
        ...(metric.baseN !== undefined && {
          baseN: metric.baseN,
        }),
        ...(metric.missingRollingPeriodLabels !== undefined && {
          missingRollingPeriodLabels: metric.missingRollingPeriodLabels,
        }),
      })) || [];

    return {
      id: variable.variableId,
      name: variable.variableLabel,
      data: dataPoints,
      color: getChartColor(index),
      visible: true,
    };
  });

  return {
    series,
    categories,
    responsesMinDate: apiData.responsesMinDate,
    responsesMaxDate: apiData.responsesMaxDate,
    effectiveRollingPeriodCount: apiData.effectiveRollingPeriodCount,
    rollingPeriodsWarningMessage: apiData.rollingPeriodsWarningMessage,
    comparisonRollingLabel: apiData.comparisonRollingLabel,
    missingRollingPeriodLabels: apiData.missingRollingPeriodLabels,
  };
};
